import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Avatar,
  Chip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";

import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;
    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Users = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { profileImage } = loggedInUser;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;
      const onCompanyUser = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_USER", payload: +data.userId });
        }
      };
      socket.on(`company-${companyId}-user`, onCompanyUser);
      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderProfileImage = (user) => {
    const src =
      user.id === loggedInUser.id
        ? (profileImage ? `${backendUrl}/public/avatar/${profileImage}` : whatsappIcon)
        : (user.profileImage ? `${backendUrl}/public/avatar/${user.profileImage}` : whatsappIcon);

    return <Avatar src={src} alt={user.name} sx={{ width: 40, height: 40 }} />;
  };

  const getProfileChip = (profile) => {
    const profileMap = {
      admin: { label: "Administrador", color: "#3b82f6" },
      supervisor: { label: "Supervisor", color: "#8b5cf6" },
      user: { label: "Usuário", color: "#64748b" }
    };

    const config = profileMap[profile] || { label: profile, color: "#64748b" };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          bgcolor: `${config.color}15`,
          color: config.color,
          border: `1px solid ${config.color}30`
        }}
      />
    );
  };

  if (loggedInUser.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
        key={i18n.language}
      />

      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <PeopleIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("users.title")} ({users.length})
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerencie os usuários do sistema
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  fontSize: "0.875rem",
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                  "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                }
              }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenUserModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                textTransform: "none",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("users.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
              Todos os Usuários
            </Typography>
          </Box>

          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.ID")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.status")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    Avatar
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.name")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.email")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.profile")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.startWork")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.endWork")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.7rem", color: "text.secondary", letterSpacing: "0.5px", bgcolor: "background.paper" }}>
                    {i18n.t("users.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      "&:hover": { bgcolor: "action.hover", transition: "background-color 0.2s" }
                    }}
                  >
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: PRIMARY_MAIN }}>
                        #{user.id}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <UserStatusIcon user={user} />
                    </TableCell>
                    <TableCell align="center">
                      {renderProfileImage(user)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                        {user.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getProfileChip(user.profile)}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {user.startWork || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                        {user.endWork || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEditUser(user)}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: PRIMARY_MAIN, bgcolor: "rgba(25, 118, 210, 0.08)" }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingUser(user);
                          }}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.08)" }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {loadingMore && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && !loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, gap: 2 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                {i18n.t("loading")}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Users;